import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const CalendarView = ({ certificates, onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1));

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();
  };

  const getCertificatesForDate = (day) => {
    const dateStr = `${String(day)?.padStart(2, '0')}/${String(currentDate?.getMonth() + 1)?.padStart(2, '0')}/${currentDate?.getFullYear()}`;
    return certificates?.filter(cert => cert?.expiryDate === dateStr);
  };

  const getStatusColor = (daysUntilExpiry) => {
    if (daysUntilExpiry < 0 || daysUntilExpiry <= 14) return 'var(--color-error)';
    if (daysUntilExpiry <= 30) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthName = currentDate?.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const days = [];
  for (let i = 0; i < firstDay; i++) {
    days?.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days?.push(i);
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground font-heading">Expiry Calendar</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrevMonth}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Previous month"
          >
            <Icon name="ChevronLeft" size={20} color="var(--color-muted-foreground)" />
          </button>
          <span className="text-sm font-medium text-foreground font-caption min-w-[140px] text-center">
            {monthName}
          </span>
          <button
            onClick={handleNextMonth}
            className="h-10 w-10 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Next month"
          >
            <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
          <div key={day} className="text-center p-2">
            <span className="text-xs font-medium text-muted-foreground font-caption">{day}</span>
          </div>
        ))}

        {days?.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }

          const certs = getCertificatesForDate(day);
          const hasExpiries = certs?.length > 0;

          return (
            <button
              key={day}
              onClick={() => hasExpiries && onDateClick(day, certs)}
              className={`aspect-square p-2 rounded-lg border transition-smooth ${
                hasExpiries
                  ? 'border-accent bg-accent/5 hover:bg-accent/10 cursor-pointer' :'border-border bg-input'
              }`}
            >
              <div className="flex flex-col items-center justify-center h-full">
                <span className="text-sm font-medium text-foreground font-data mb-1">{day}</span>
                {hasExpiries && (
                  <div className="flex gap-1">
                    {certs?.slice(0, 3)?.map((cert, i) => (
                      <div
                        key={i}
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: getStatusColor(cert?.daysUntilExpiry) }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-6 pt-6 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: 'var(--color-error)' }} />
          <span className="text-xs text-muted-foreground font-caption">Expired/Critical</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: 'var(--color-warning)' }} />
          <span className="text-xs text-muted-foreground font-caption">Expiring Soon</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
          <span className="text-xs text-muted-foreground font-caption">Valid</span>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;